'use client'

// The client half of a running study session ("How long do you have?", Phase 2):
// which session this page belongs to (its ?session= marker) and what the
// session says about itself. Shared by the session bar and the hand-off.

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'

import { SESSION_PARAM, parseSessionMarker, type SessionPosition } from '@/lib/study-session/marker'
import type { BlockOutcome, StudySessionView } from '@/lib/study-session/session-plan'

/** The page's marker, or null when it wasn't opened from a session. */
export function useSessionMarker(): ({ id: string; raw: string } & SessionPosition) | null {
  const params = useSearchParams()
  const raw = params?.get(SESSION_PARAM) ?? null
  const parsed = parseSessionMarker(raw)
  return parsed && raw ? { ...parsed, raw: raw.trim() } : null
}

/** GET the session's view. Null while loading, on error, or without an id. */
export function useStudySessionView(id: string | null): StudySessionView | null {
  const [view, setView] = useState<StudySessionView | null>(null)
  useEffect(() => {
    if (!id) {
      setView(null)
      return
    }
    let cancelled = false
    fetch(`/api/study-session/${encodeURIComponent(id)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? (r.json() as Promise<StudySessionView>) : null))
      .then((v) => {
        if (!cancelled) setView(v)
      })
      .catch(() => {
        if (!cancelled) setView(null)
      })
    return () => {
      cancelled = true
    }
  }, [id])
  return view
}

type MoveResponse = { goto?: string; error?: string }

async function post(url: string, body?: unknown): Promise<MoveResponse> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-store',
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as MoveResponse
  // A 409 (the session already ended) still says where to go.
  if (!res.ok && !data.goto) throw new Error(data.error ?? `HTTP ${res.status}`)
  return data
}

/** Hand off from a page to the next one; resolves to where to go. */
export function advanceSession(
  id: string,
  from: SessionPosition,
  outcome: BlockOutcome | null,
): Promise<MoveResponse> {
  return post(`/api/study-session/${encodeURIComponent(id)}/advance`, {
    block: from.block,
    step: from.step,
    outcome,
  })
}

export function endSession(id: string): Promise<MoveResponse> {
  return post(`/api/study-session/${encodeURIComponent(id)}/end`)
}

/** A busy flag + error around one of the moves above. */
export function useSessionMove() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = useCallback(async (move: () => Promise<MoveResponse>) => {
    setBusy(true)
    setError(null)
    try {
      const r = await move()
      return r.goto ?? '/dashboard'
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't reach the server. Try again?")
      setBusy(false)
      return null
    }
  }, [])
  return { busy, error, run }
}
